import React, { useEffect, useMemo, useState } from "react";
import Breadcrumb from "../../../CommonComponents/BreadCrumb/BreadCrumb";
import CustomTable from "../../../CommonComponents/Table/CustomTable";
import CommonModal from "../../../Modals/CommonModal";
import { networkService } from "../../../config/axios";
import "../admin.css";
import "../ApiUsage/adminApiUsage.css";
import "./adminHostedDocuments.css";

const STATUS_LABELS = {
  quarantined: "Scanning",
  clean: "Clean",
  rejected: "Rejected",
  disabled: "Disabled",
};

const formatSize = (bytes) => (bytes ? `${(Number(bytes) / 1024 / 1024).toFixed(1)} MB` : "—");

const AdminHostedDocumentsPage = () => {
  const [documents, setDocuments] = useState([]);
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [disabling, setDisabling] = useState(null);
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await networkService("admin/hosted_documents_list.json", "GET");
      setDocuments(data?.hosted_documents || []);
    } catch (e) {
      setError("Could not load hosted documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const counts = useMemo(
    () =>
      documents.reduce(
        (acc, doc) => ({ ...acc, total: acc.total + 1, [doc.status]: (acc[doc.status] || 0) + 1 }),
        { total: 0 }
      ),
    [documents]
  );

  const visible = useMemo(
    () => (status ? documents.filter((doc) => doc.status === status) : documents),
    [documents, status]
  );

  const confirmDisable = async () => {
    if (!disabling) return;
    setSaving(true);
    setError("");
    try {
      const { data } = await networkService(`admin/hosted_documents/${disabling.id}/disable.json`, "POST", { reason });
      setDocuments((current) => current.map((doc) => (doc.id === disabling.id ? { ...doc, ...data } : doc)));
      setDisabling(null);
      setReason("");
    } catch (e) {
      setError("Could not disable the file.");
    } finally {
      setSaving(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: "Uploaded",
        accessor: "created_at",
        Cell: ({ value }) => (value ? new Date(value).toLocaleString() : "—"),
      },
      {
        Header: "Account",
        accessor: (row) => row.user?.email || "",
        id: "account",
        Cell: ({ row }) => (
          <div className="kz-hd-stack">
            <span>{row.original.user?.name || "—"}</span>
            <small>
              {row.original.user?.email}
              {row.original.company ? ` · ${row.original.company}` : ""}
            </small>
            {row.original.user?.banned && <span className="kz-hd-badge kz-hd-badge--disabled">Banned</span>}
          </div>
        ),
      },
      {
        Header: "File",
        accessor: "original_filename",
        Cell: ({ row }) => (
          <div className="kz-hd-stack">
            <span className="kz-hd-filename">{row.original.original_filename}</span>
            <small>
              {formatSize(row.original.byte_size)}
              {row.original.sha256 && (
                <span className="kz-hd-sha" title={row.original.sha256}> · SHA-256 {row.original.sha256.slice(0, 12)}…</span>
              )}
            </small>
          </div>
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ row }) => (
          <div className="kz-hd-stack">
            <span className={`kz-hd-badge kz-hd-badge--${row.original.status}`}>
              {STATUS_LABELS[row.original.status] || row.original.status}
            </span>
            {(row.original.rejection_reason || row.original.disabled_reason) && (
              <small>{row.original.rejection_reason || row.original.disabled_reason}</small>
            )}
          </div>
        ),
      },
      {
        Header: "Scan log",
        accessor: "scan_result",
        disableSortBy: true,
        Cell: ({ value, row }) =>
          value ? (
            <details className="kz-hd-scan">
              <summary>{row.original.scan_engine || "Details"}</summary>
              <pre>{value}</pre>
            </details>
          ) : (
            "—"
          ),
      },
      {
        Header: "LaserLINX",
        accessor: "laserlinks",
      },
      {
        Header: "",
        id: "actions",
        disableSortBy: true,
        Cell: ({ row }) =>
          row.original.status !== "disabled" ? (
            <button
              type="button"
              className="kz-hd-disable"
              onClick={() => {
                setReason("");
                setDisabling(row.original);
              }}
            >
              Disable
            </button>
          ) : null,
      },
    ],
    []
  );

  return (
    <div className="admin-module">
      <div className="admin-api-usage-page admin-content">
        <Breadcrumb string="Admin / Hosted Documents" />

        <div className="container admin-api-usage-container">
          <div className="admin-api-usage-summary">
            <div className="admin-api-usage-card">
              <span className="admin-api-usage-card-label">All uploads</span>
              <span className="admin-api-usage-card-value">{counts.total}</span>
            </div>
            <div className="admin-api-usage-card">
              <span className="admin-api-usage-card-label">Clean and live</span>
              <span className="admin-api-usage-card-value">{counts.clean || 0}</span>
            </div>
            <div className="admin-api-usage-card">
              <span className="admin-api-usage-card-label">Still scanning</span>
              <span className="admin-api-usage-card-value">{counts.quarantined || 0}</span>
            </div>
            <div className="admin-api-usage-card">
              <span className="admin-api-usage-card-label">Rejected or disabled</span>
              <span className="admin-api-usage-card-value">{(counts.rejected || 0) + (counts.disabled || 0)}</span>
            </div>
          </div>

          <div className="admin-api-usage-filters">
            <select className="admin-api-usage-filter-select" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">All statuses</option>
              <option value="quarantined">Scanning</option>
              <option value="clean">Clean</option>
              <option value="rejected">Rejected</option>
              <option value="disabled">Disabled</option>
            </select>
            <span className="kz-hd-hint">To suspend an account, ban the user from the Users page.</span>
          </div>

          {error && <div className="alert alert-danger small">{error}</div>}

          <div className="top-parent admin-api-usage-table-wrapper kz-hd-table">
            {loading ? (
              <p className="admin-api-usage-loading">Loading…</p>
            ) : visible.length === 0 ? (
              <p className="admin-api-usage-loading">No hosted documents.</p>
            ) : (
              <CustomTable data={visible} columnData={columns} />
            )}
          </div>
        </div>
      </div>

      <CommonModal
        title="Disable hosted file"
        modalStatus={!!disabling}
        changeActiveStatus={() => setDisabling(null)}
        centered
        showFooter={false}
      >
        <p className="mb-2">
          <strong>{disabling?.original_filename}</strong> will stop downloading for every recipient straight away. This
          cannot be undone from here.
        </p>
        <label className="kz-hd-label" htmlFor="kz-hd-reason">Reason (kept in the audit log)</label>
        <input
          id="kz-hd-reason"
          className="form-control mb-3"
          value={reason}
          placeholder="For example: reported as phishing"
          onChange={(e) => setReason(e.target.value)}
        />
        <div className="d-flex justify-content-end gap-2">
          <button type="button" className="kz-hd-cancel" onClick={() => setDisabling(null)}>
            Cancel
          </button>
          <button type="button" className="kz-hd-confirm" disabled={saving} onClick={confirmDisable}>
            Disable file
          </button>
        </div>
      </CommonModal>
    </div>
  );
};

export default AdminHostedDocumentsPage;
